import {
  getLivingCrossingMeta,
  getLivingCrossingNodes,
} from "@/content";
import "./LivingCrossing.css";

/**
 * Dominant Living Crossing instrument.
 * High-res field asset as material underlay; live SVG/HTML labels in chambers.
 */
export function LivingCrossing() {
  const meta = getLivingCrossingMeta();
  const nodes = getLivingCrossingNodes();
  const byKey = Object.fromEntries(nodes.map((n) => [n.key, n]));

  return (
    <div
      className="living-model"
      role="img"
      aria-labelledby="model-title"
      aria-describedby="model-description model-summary"
    >
      <div className="model-safe-text">
        <strong id="model-title">{meta.livingCrossingTitle}</strong>
        <span id="model-description">{meta.livingCrossingDescription}</span>
      </div>

      <p className="visually-hidden" id="model-summary">
        {meta.livingCrossingSummary}
      </p>

      <div className="model-stage">
        <div className="model-field" aria-hidden="true" />

        <svg
          className="model-overlay"
          viewBox="0 0 640 560"
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="chamberPale" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="rgba(255,255,255,0.5)" />
              <stop offset="26%" stopColor="rgba(214,238,231,0.4)" />
              <stop offset="62%" stopColor="rgba(126,186,175,0.3)" />
              <stop offset="100%" stopColor="rgba(20,84,78,0.34)" />
            </linearGradient>
            <linearGradient id="rimLight" x1="0%" y1="0%" x2="60%" y2="100%">
              <stop offset="0%" stopColor="rgba(255,255,255,0.92)" />
              <stop offset="38%" stopColor="rgba(221,199,142,0.62)" />
              <stop offset="100%" stopColor="rgba(182,137,56,0.32)" />
            </linearGradient>
            <radialGradient id="jewelFill" cx="38%" cy="28%" r="74%">
              <stop offset="0%" stopColor="rgba(158,220,208,0.72)" />
              <stop offset="32%" stopColor="rgba(26,116,107,0.9)" />
              <stop offset="72%" stopColor="rgba(11,68,63,0.96)" />
              <stop offset="100%" stopColor="rgba(4,32,30,0.98)" />
            </radialGradient>
            <filter id="chamberDepth" x="-18%" y="-18%" width="136%" height="136%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="4" result="blur" />
              <feOffset dy="3" result="off" />
              <feFlood floodColor="rgba(4,32,30,0.34)" />
              <feComposite in2="off" operator="in" result="shadow" />
              <feMerge>
                <feMergeNode in="shadow" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <filter id="glassSoften" x="-10%" y="-10%" width="120%" height="120%">
              <feGaussianBlur stdDeviation="0.6" />
            </filter>
            <clipPath id="stageClip">
              <ellipse cx="320" cy="280" rx="290" ry="250" />
            </clipPath>
          </defs>

          <g clipPath="url(#stageClip)">
            {/* Outer orbital gold seams */}
            <ellipse
              cx="320"
              cy="280"
              rx="268"
              ry="232"
              fill="none"
              stroke="rgba(182,137,56,0.38)"
              strokeWidth="1.4"
            />
            <ellipse
              cx="320"
              cy="280"
              rx="230"
              ry="198"
              fill="none"
              stroke="rgba(221,199,142,0.28)"
              strokeWidth="1"
              strokeDasharray="3 7"
            />

            {/* Organic chambers: illuminated rims, separated edges, translucent depth */}
            <g className="chamber-group">
              <path
                className="chamber"
                d="M320 78 C410 92 485 160 478 248 C472 320 400 355 320 348 C240 355 168 320 162 248 C155 160 230 92 320 78 Z"
                fill="url(#chamberPale)"
                stroke="url(#rimLight)"
                strokeWidth="2.6"
                filter="url(#chamberDepth)"
                opacity="0.94"
              />
              <path
                d="M320 78 C410 92 485 160 478 248 C472 320 400 355 320 348 C240 355 168 320 162 248 C155 160 230 92 320 78 Z"
                fill="none"
                stroke="rgba(4,32,30,0.28)"
                strokeWidth="0.8"
                transform="translate(0 3)"
                filter="url(#glassSoften)"
              />
            </g>
            <g className="chamber-group">
              <path
                className="chamber"
                d="M128 210 C185 145 255 155 300 210 C255 270 180 310 118 285 C90 260 95 230 128 210 Z"
                fill="url(#chamberPale)"
                stroke="url(#rimLight)"
                strokeWidth="2.2"
                filter="url(#chamberDepth)"
                opacity="0.92"
              />
            </g>
            <g className="chamber-group">
              <path
                className="chamber"
                d="M512 210 C545 230 548 265 520 290 C450 320 370 270 340 210 C385 155 455 145 512 210 Z"
                fill="url(#chamberPale)"
                stroke="url(#rimLight)"
                strokeWidth="2.2"
                filter="url(#chamberDepth)"
                opacity="0.92"
              />
            </g>
            <g className="chamber-group">
              <path
                className="chamber"
                d="M320 330 C385 345 430 400 405 455 C370 495 270 495 235 455 C210 400 255 345 320 330 Z"
                fill="url(#chamberPale)"
                stroke="url(#rimLight)"
                strokeWidth="2.3"
                filter="url(#chamberDepth)"
                opacity="0.94"
              />
            </g>

            {/* Topographic contours inside chambers */}
            <g fill="none" stroke="rgba(13,76,71,0.22)" strokeWidth="1">
              <path d="M250 160 C290 140 350 140 390 165" />
              <path d="M230 200 C280 175 360 175 410 205" />
              <path d="M245 250 C295 230 345 230 395 255" />
              <path d="M170 230 C200 215 230 220 250 245" />
              <path d="M390 230 C420 215 460 220 490 245" />
              <path d="M275 370 C310 355 350 355 385 375" />
            </g>

            {/* Central Choose jewel */}
            <circle
              cx="320"
              cy="268"
              r="72"
              fill="url(#jewelFill)"
              stroke="url(#rimLight)"
              strokeWidth="3"
              filter="url(#chamberDepth)"
            />
            <circle
              cx="320"
              cy="268"
              r="69"
              fill="none"
              stroke="rgba(255,255,255,0.34)"
              strokeWidth="1"
            />
            <circle
              cx="320"
              cy="268"
              r="52"
              fill="none"
              stroke="rgba(255,255,255,0.26)"
              strokeWidth="1.2"
            />
            <ellipse
              cx="300"
              cy="234"
              rx="32"
              ry="17"
              fill="rgba(255,255,255,0.16)"
              filter="url(#glassSoften)"
            />
            {/* Frost etch inside jewel */}
            <g stroke="rgba(255,255,255,0.22)" strokeWidth="0.9" fill="none">
              <path d="M300 240 C310 255 330 255 340 240" />
              <path d="M292 268 C310 258 330 258 348 268" />
              <path d="M300 296 C315 285 325 285 340 296" />
            </g>

            {/* Pearl / gold connector nodes */}
            <g className="pearl-nodes">
              <circle cx="320" cy="108" r="6.5" fill="#fbf7ee" stroke="#b68938" strokeWidth="1.5" />
              <circle cx="145" cy="250" r="6.5" fill="#fbf7ee" stroke="#b68938" strokeWidth="1.5" />
              <circle cx="495" cy="250" r="6.5" fill="#fbf7ee" stroke="#b68938" strokeWidth="1.5" />
              <circle cx="320" cy="448" r="6.5" fill="#fbf7ee" stroke="#b68938" strokeWidth="1.5" />
              <circle cx="230" cy="170" r="5" fill="#fbf7ee" stroke="#b68938" strokeWidth="1.2" />
              <circle cx="410" cy="170" r="5" fill="#fbf7ee" stroke="#b68938" strokeWidth="1.2" />
              <circle cx="318" cy="106" r="2" fill="rgba(255,255,255,0.95)" />
              <circle cx="143" cy="248" r="2" fill="rgba(255,255,255,0.95)" />
              <circle cx="493" cy="248" r="2" fill="rgba(255,255,255,0.95)" />
              <circle cx="318" cy="446" r="2" fill="rgba(255,255,255,0.95)" />
            </g>
          </g>
        </svg>

        {/* Live labels seated in chambers — not opaque UI pills */}
        <div className="chamber-label bound">
          <b>{byKey.bound.label}</b>
          <small>{byKey.bound.text}</small>
        </div>
        <div className="chamber-label hold">
          <b>{byKey.hold.label}</b>
          <small>{byKey.hold.text}</small>
        </div>
        <div className="chamber-label see">
          <b>{byKey.see.label}</b>
          <small>{byKey.see.text}</small>
        </div>
        <div className="chamber-label grow">
          <b>{byKey.grow.label}</b>
          <small>{byKey.grow.text}</small>
        </div>
        <div className="chamber-label choose">
          <b>Choose</b>
          <small>Transition Commons</small>
        </div>
        <div className="chamber-label cross">
          <b>{byKey.cross.label}</b>
          <small>{byKey.cross.text}</small>
        </div>
        <div className="chamber-label shift">
          <b>{byKey.shift.label}</b>
          <small>{byKey.shift.text}</small>
        </div>
        <div className="chamber-label learn">
          <b>{byKey.learn.label}</b>
          <small>{byKey.learn.text}</small>
        </div>
      </div>

      <div className="model-linear">
        <p>
          <strong>Linear reading.</strong> Bound defines safe-and-just limits. See senses the living
          system. Hold protects what must endure; Cross retires what no longer serves; Grow
          cultivates what comes next. Choose brings decisions into the Transition Commons. Shift
          coordinates action; Learn closes the loop.
        </p>
      </div>
    </div>
  );
}
